import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, PhoneMissed } from 'lucide-react';
import clsx from 'clsx';

import { apiErrorMessage } from '@/lib/api';
import {
  callBucket,
  callWhen,
  markCall,
  undoCall,
  RESULT_LABEL,
  type CallResult,
  type CallSummary,
  type CallView,
} from '@/lib/calls';
import { patchCachedCall } from '@/lib/establishments';

type OnChange = (establishmentId: string, summary: CallSummary | null, call?: CallView) => void;

/**
 * Отметка звонка в одно касание: сразу на сервер, строка уходит в свою группу,
 * а в тосте — «Отменить» на случай, если ошибся кнопкой (сервер даёт 15 минут).
 */
export function useCallMarker(onChange: OnChange) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const inFlight = useRef(false);

  const undo = async (callId: string) => {
    try {
      const res = await undoCall(callId);
      patchCachedCall(res.establishmentId, res.summary);
      onChange(res.establishmentId, res.summary);
      toast.success('Отметка отменена');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось отменить отметку'));
    }
  };

  const mark = async (establishmentId: string, result: CallResult, comment?: string): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusyId(establishmentId);
    try {
      const res = await markCall(establishmentId, result, comment);
      patchCachedCall(establishmentId, res.summary);
      onChange(establishmentId, res.summary, res.call);
      toast(
        (t) => (
          <span className="flex items-center gap-3 text-sm">
            <span>
              Отмечено: <b className="font-medium">{RESULT_LABEL[result].toLowerCase()}</b>
            </span>
            <button
              type="button"
              className="rounded-md px-2 py-1 font-medium text-orange hover:bg-orange/10"
              onClick={() => {
                toast.dismiss(t.id);
                void undo(res.call.id);
              }}
            >
              Отменить
            </button>
          </span>
        ),
        { duration: 6000 },
      );
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось отметить звонок'));
      return false;
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  };

  return { mark, undo, busyId };
}

/** Две кнопки итога. compact — значки для таблицы, подписи — в title и для экранного диктора. */
export function CallMarkButtons({
  onMark,
  busy,
  compact,
  className,
}: {
  onMark: (result: CallResult) => void;
  busy?: boolean;
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={clsx('flex shrink-0 gap-1.5', className)}>
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onMark('ANSWERED');
        }}
        title="Дозвонился"
        aria-label="Отметить: дозвонился"
        className={clsx(
          'inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 font-medium text-emerald-700 hover:bg-emerald-100 active:bg-emerald-100 disabled:opacity-50',
          compact ? 'h-8 w-8' : 'min-h-[40px] px-3 text-sm',
        )}
      >
        <Check size={compact ? 15 : 16} />
        {compact ? null : 'Дозвонился'}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onMark('NO_ANSWER');
        }}
        title="Не ответил"
        aria-label="Отметить: не ответил"
        className={clsx(
          'inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 font-medium text-amber-800 hover:bg-amber-100 active:bg-amber-100 disabled:opacity-50',
          compact ? 'h-8 w-8' : 'min-h-[40px] px-3 text-sm',
        )}
      >
        <PhoneMissed size={compact ? 15 : 16} />
        {compact ? null : 'Не ответил'}
      </button>
    </div>
  );
}

/**
 * Прозвон строки одной фразой — тем звонком, по которому строка попала в группу
 * за период: дозвонились утром и не взяли трубку вечером — «дозвонились, утром».
 * Звонков за период нет — последний звонок приглушённо; кто звонил — главному.
 */
export function LastCall({
  call,
  since,
  showWho,
  wrap,
  className,
}: {
  call: CallSummary | null | undefined;
  /** Начало периода прозвона (мс). */
  since: number;
  showWho?: boolean;
  /** В таблице — переносить, а не обрезать. */
  wrap?: boolean;
  className?: string;
}) {
  if (!call) return <span className={clsx('text-xs text-ink/40', className)}>Ещё не звонили</span>;
  const bucket = callBucket(call, since);
  const answered = bucket === 'none' ? call.lastResult === 'ANSWERED' : bucket === 'answered';
  const at = bucket === 'answered' ? call.answeredAt! : bucket === 'no_answer' ? call.noAnswerAt! : call.lastAt;
  // Имя знаем только у последнего звонка.
  const who = showWho && at === call.lastAt ? call.lastBy : null;
  return (
    <span className={clsx('inline-flex min-w-0 items-start gap-1.5 text-xs', bucket === 'none' && 'opacity-60', className)}>
      {answered ? (
        <Check size={13} className="mt-px shrink-0 text-emerald-600" />
      ) : (
        <PhoneMissed size={13} className="mt-px shrink-0 text-amber-600" />
      )}
      <span className={clsx('min-w-0', !wrap && 'truncate')}>
        {bucket === 'none' ? <span className="text-ink/50">последний: </span> : null}
        <span className={answered ? 'text-emerald-700' : 'text-amber-800'}>
          {answered ? 'дозвонились' : 'не ответили'}
        </span>
        <span className="text-ink/50">
          {' '}
          · {callWhen(at)}
          {who ? ` · ${who}` : ''}
        </span>
      </span>
    </span>
  );
}
